import {useCallback, useEffect} from "react";
import {SET_DMS_STATUS, useDmsStatusDispatch} from "../dmsstatus/DmsStatusContext";
import camelcaseKeysDeep from "camelcase-keys-deep";
import {clientLog} from "../clientLog";
import {kickOffDmsStatusWorker} from "../webworkers/kickOffDmsStatusWorker";
import {refreshToken} from "@ekultur/authentication";

/** Sends a pause or resume action to the DMS status worker. */
const sendVisibilityAction = (action) => {
    if (window._dmsStatusWorkerInstance) {
        window._dmsStatusWorkerInstance.postMessage(
            JSON.stringify({channel: 'worker', action})
        );
    }
};

/**
 * Listener used to listen for, and parse, messages sent by the DMS status web worker.
 * @returns {JSX.Element}
 * @constructor
 */
export const DMSStatusWorkerListener = () => {

    const dmsStatusDispatch = useDmsStatusDispatch();

    const isValidMessage = event => {
        if (!event.data) {
            return false;
        }
        const {channel, action} = event.data;
        return !(!channel || channel !== 'client' || !action || action === '');
    };

    /**
     * Method used to parse messages received from the DMS status worker.
     * Listening to "channel" => "client", as messages tagged with this
     * information is used to trigger client-code, outside the web worker.
     * @param event
     */
    const workerMessageParser = useCallback(event => {
        if (!isValidMessage(event)) {
            return;
        }

        const data = event.data;
        const {action} = data;

        if (action === 'start') {
            clientLog('info', 'worker started', 'DMSStatusWorkerListener');
        } else if (action === 'refresh') {
            refreshToken({
                apiGateway: window._env_.REACT_APP_API_GATEWAY,
                onSuccess: () => {
                    clientLog('info', 'dmsStatusWorker: token refreshed', 'DMSStatusWorkerListener');
                },
            });
        } else if (action === 'statusUpdate') {
            dmsStatusDispatch({
                type: SET_DMS_STATUS,
                dmsStatus: camelcaseKeysDeep(data.data),
            });
        } else if (action === 'expiredToken') {
            // 401 received — refresh the token first, then restart the worker
            // so it picks up fresh credentials from localStorage.
            clientLog('warn', 'dmsStatusWorker: token expired — refreshing before restart', 'DMSStatusWorkerListener');
            refreshToken({
                apiGateway: window._env_.REACT_APP_API_GATEWAY,
                onSuccess: () => {
                    clientLog('info', 'dmsStatusWorker: token refreshed — restarting worker', 'DMSStatusWorkerListener');
                    kickOffDmsStatusWorker();
                },
            });
        }
    }, [dmsStatusDispatch]);

    /**
     * Hook used to add an event listener that listens for messages from the DMS status web worker.
     * Also wires up a visibilitychange handler so polling is paused when the tab is hidden
     * and resumed (with an immediate poll) when the tab becomes visible again.
     */
    useEffect(() => {
        window._dmsStatusWorkerInstance.onmessage = workerMessageParser;

        const handleVisibilityChange = () => {
            sendVisibilityAction(document.hidden ? 'pause' : 'resume');
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            // Detach the worker handler so the stale closure is released and
            // cannot fire after unmount or when the effect re-runs.
            if (window._dmsStatusWorkerInstance) {
                window._dmsStatusWorkerInstance.onmessage = null;
            }
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [workerMessageParser]);

    return <></>;
};
